import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import inspectionsService from "../services/inspectionsService";
import styles from './BuildingManagerCalendar.module.css';

// Create drag and drop enabled calendar
const DragAndDropCalendar = withDragAndDrop(Calendar);

const locales = {
  'en-US': enUS
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Property monthly inspection status color mapping (matching Laravel config)
const MONTHLY_INSPECTION_STATUS_COLORS = {
  0: 'black',      // Other
  1: 'blue',       // Upcoming
  2: 'purple',     // Inspection_Completed
  3: 'green',      // Completed
  4: 'red',        // Overdue
  5: 'orange',     // To_Be_Published
  6: 'burlywood',  // Inspection_Scheduled
};

// Helper function to get property background color
const getPropertyBackgroundColor = (property) => {
  const status = property.monthly_inspection_status;
  return MONTHLY_INSPECTION_STATUS_COLORS[status] || '#e8f4f8'; // default color
};

// Helper function to get text color based on background color for readability
const getTextColorForBackground = (backgroundColor) => {
  const darkBackgrounds = ['black', 'blue', 'purple', 'green', 'red'];
  return darkBackgrounds.includes(backgroundColor) ? '#ffffff' : '#000000';
};

const BuildingManagerCalendar = () => {
  const { buildingManagerId } = useParams();
  const navigate = useNavigate();

  const [buildingManager, setBuildingManager] = useState(null);
  const [calendarInspections, setCalendarInspections] = useState([]);
  const [statusOverview, setStatusOverview] = useState({
    all: 0,
    upcoming: 0,
    past: 0,
  });
  const [propertiesByRoute, setPropertiesByRoute] = useState({});
  const [routes, setRoutes] = useState({});
  const [types, setTypes] = useState({});
  const [months, setMonths] = useState([]);
  const [routesNames, setRoutesNames] = useState({});
  const [buildingManagers, setBuildingManagers] = useState({});
  const [filteredBuildingManagerId, setFilteredBuildingManagerId] = useState('0');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'upcoming', 'past' - for DataTable filter
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingInspection, setEditingInspection] = useState(null);
  const [formData, setFormData] = useState({
    property_id: '',
    property_title: '',
    building_manager_id: buildingManagerId,
    type_id: '',
    day: '',
    time_from: '',
    time_to: '',
    description: '',
    updated_at: ''
  });
  const [propertySearchValue, setPropertySearchValue] = useState('');
  const [propertySearchResults, setPropertySearchResults] = useState([]);
  const [showPropertyTypeahead, setShowPropertyTypeahead] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [statusDescriptionCollapsed, setStatusDescriptionCollapsed] = useState(true); // Collapsed by default like Laravel
  const [dragData, setDragData] = useState(null); // Store drag data for calendar drop
  const [dragMousePos, setDragMousePos] = useState({ x: 0, y: 0 }); // Track mouse position during drag
  const [calendarDate, setCalendarDate] = useState(new Date()); // Track calendar's current view date
  const draggedInspectionIdRef = useRef(null); // Track currently dragged inspection for trash drop detection
  const dragMousePosRef = useRef({ x: 0, y: 0 }); // Track mouse position in ref for dragend handler
  const loadDataRef = useRef(null); // Store loadData function reference
  const loadCalendarEventsRef = useRef(null); // Store loadCalendarEvents function reference
  const loadDataInFlightRef = useRef(false); // Prevent duplicate loadData calls (React StrictMode)
  const loadCalendarEventsInFlightRef = useRef(false); // Prevent duplicate calendar event calls

  // Server-side paginated table state
  const [tableData, setTableData] = useState([]);
  const [tablePagination, setTablePagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    per_page: 10,
  });
  const [tableLoading, setTableLoading] = useState(false);
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingManagerId]);

  // Debug modal state
  useEffect(() => {
    console.log('📋 [MODAL STATE] showEditModal:', showEditModal, 'editingInspection:', editingInspection?.id);
  }, [showEditModal, editingInspection]);

  // Add global drag listeners for trash area visual feedback (Laravel eventDragStop pattern)
  useEffect(() => {
    let dragOverTrash = false;

    const handleGlobalMouseMove = (e) => {
      // Track mouse position for event drag detection (Laravel pattern: pageX/pageY)
      const pos = { 
        x: e.pageX || e.clientX + (window.pageXOffset || document.documentElement.scrollLeft),
        y: e.pageY || e.clientY + (window.pageYOffset || document.documentElement.scrollTop)
      };
      setDragMousePos(pos);
      dragMousePosRef.current = pos; // Also update ref for dragend handler
      
      // Also check if we're dragging an inspection and update visual feedback
      if (draggedInspectionIdRef.current) {
        const trashEl = document.getElementById('calendarTrash');
        if (trashEl) {
          const rect = trashEl.getBoundingClientRect();
          const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
          const scrollY = window.pageYOffset || document.documentElement.scrollTop;
          const x1 = rect.left + scrollX;
          const x2 = rect.right + scrollX;
          const y1 = rect.top + scrollY;
          const y2 = rect.bottom + scrollY;
          
          const isOverTrash = pos.x >= x1 && pos.x <= x2 && pos.y >= y1 && pos.y <= y2;
          if (isOverTrash && !dragOverTrash) {
            dragOverTrash = true;
            trashEl.style.backgroundColor = '#ffcccc';
            trashEl.style.borderColor = '#ff0000';
            trashEl.style.transform = 'scale(1.05)';
          } else if (!isOverTrash && dragOverTrash) {
            dragOverTrash = false;
            trashEl.style.backgroundColor = '#f5f5f5';
            trashEl.style.borderColor = '#ccc';
            trashEl.style.transform = 'scale(1)';
          }
        }
      }
      
      const trashEl = document.getElementById('calendarTrash');
      if (trashEl) {
        const rect = trashEl.getBoundingClientRect();
        const isOverTrash = e.clientX >= rect.left && e.clientX <= rect.right && 
                           e.clientY >= rect.top && e.clientY <= rect.bottom;
        
        if (isOverTrash && !dragOverTrash) {
          dragOverTrash = true;
          trashEl.style.backgroundColor = '#ffcccc';
          trashEl.style.borderColor = '#ff0000';
          trashEl.style.transform = 'scale(1.05)';
        } else if (!isOverTrash && dragOverTrash) {
          dragOverTrash = false;
          trashEl.style.backgroundColor = '#f5f5f5';
          trashEl.style.borderColor = '#ccc';
          trashEl.style.transform = 'scale(1)';
        }
      }
    };

    const handleGlobalDragOver = (e) => {
      e.preventDefault(); // Required for drop to work
      handleGlobalMouseMove(e);
    };

    const handleGlobalDragEnd = async (e) => {
      const trashEl = document.getElementById('calendarTrash');
      if (trashEl) {
        trashEl.style.backgroundColor = '#f5f5f5';
        trashEl.style.borderColor = '#ccc';
        trashEl.style.transform = 'scale(1)';
        dragOverTrash = false;

        // Check if drag ended over trash area and we have a dragged inspection
        const currentDraggedId = draggedInspectionIdRef.current;
        console.log('🏁 [DRAG END] Checking trash drop, inspectionId:', currentDraggedId);

        if (currentDraggedId) {
          const rect = trashEl.getBoundingClientRect();
          const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
          const scrollY = window.pageYOffset || document.documentElement.scrollTop;

          // Use clientX/Y for comparison with getBoundingClientRect
          const mouseX = e.clientX || (dragMousePosRef.current.x - scrollX);
          const mouseY = e.clientY || (dragMousePosRef.current.y - scrollY);

          console.log('🏁 [DRAG END] Mouse position:', { mouseX, mouseY });
          console.log('🏁 [DRAG END] Trash rect:', rect);

          // Check with tolerance for better detection
          const tolerance = 50; // pixels
          if (mouseX >= rect.left - tolerance && mouseX <= rect.right + tolerance &&
              mouseY >= rect.top - tolerance && mouseY <= rect.bottom + tolerance) {
            console.log('✅ [DRAG END] Dropped on trash area!');
            // Dropped on trash - delete inspection
            if (window.confirm('Are you sure you want to delete this inspection?')) {
              try {
                await inspectionsService.deleteInspection(currentDraggedId);
                toast.success('Inspection deleted successfully!');
                // Call loadData if available
                if (loadDataRef.current) {
                  loadDataRef.current();
                } else {
                  // Fallback: reload page after a short delay
                  setTimeout(() => window.location.reload(), 500);
                }
              } catch (error) {
                console.error('Error deleting inspection:', error);
                toast.error(error.response?.data?.message || 'Failed to delete inspection');
              }
            }
          }
        }

        // Reset dragged inspection after a short delay (allow other handlers to use it first)
        setTimeout(() => {
          draggedInspectionIdRef.current = null;
        }, 100);
      }
    };

    document.addEventListener('dragover', handleGlobalDragOver);
    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('dragend', handleGlobalDragEnd);

    return () => {
      document.removeEventListener('dragover', handleGlobalDragOver);
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('dragend', handleGlobalDragEnd);
    };
  }, []);

  // Helper function to format date from YYYY-MM-DD to mm/dd/yyyy
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        // If it's already in mm/dd/yyyy format, return as is
        if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
          return dateString;
        }
        return '';
      }
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch (error) {
      return dateString; // Return as is if parsing fails
    }
  };

  // Helper function to format time from HH:mm:ss or HH:mm to HH:mm
  const formatTimeForInput = (timeString) => {
    if (!timeString) return '';
    // If already in HH:mm format, return as is
    if (timeString.match(/^\d{2}:\d{2}$/)) {
      return timeString;
    }
    // Extract HH:mm from HH:mm:ss format
    const match = timeString.match(/^(\d{2}):(\d{2})/);
    if (match) {
      return `${match[1]}:${match[2]}`;
    }
    return timeString;
  };

  // Helper function to convert date to dd/mm/yyyy format for backend (Laravel format)
  const convertDateForBackend = (dateString) => {
    if (!dateString) return '';
    // If already in dd/mm/yyyy format, return as is
    if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return dateString;
    }
    // Convert from yyyy-mm-dd (HTML date input) to dd/mm/yyyy (Laravel format)
    const matchYMD = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (matchYMD) {
      const [, year, month, day] = matchYMD;
      return `${day}/${month}/${year}`;
    }
    // Convert from mm/dd/yyyy to dd/mm/yyyy
    const matchMDY = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (matchMDY) {
      const [, month, day, year] = matchMDY;
      return `${day}/${month}/${year}`;
    }
    // Try to parse as Date and convert to dd/mm/yyyy
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${day}/${month}/${year}`;
      }
    } catch (error) {
      console.error('Error converting date:', error);
    }
    return dateString; // Return as is if conversion fails
  };

  const loadData = async () => {
    if (loadDataInFlightRef.current) return;
    loadDataInFlightRef.current = true;
    try {
      setLoading(true);
      // Store function reference for dragend handler
      loadDataRef.current = loadData;
      const response = await inspectionsService.getBuildingManagerCalendar(buildingManagerId);
      const data = response.data || response;
      
      if (data.buildingManager) {
        setBuildingManager(data.buildingManager);
      }
      
      // Note: inspections are no longer loaded here - they come from the calendar-events endpoint
      // Status overview is provided by the paginated table endpoint
      
      if (data.propertiesByRoute) {
        setPropertiesByRoute(data.propertiesByRoute);
      }
      
      if (data.routes) {
        setRoutes(data.routes);
      }
      
      if (data.types) {
        setTypes(data.types);
      }
      
      if (data.months) {
        setMonths(data.months);
      }
      
      if (data.routesNames) {
        setRoutesNames(data.routesNames);
        // routesNames will be used for calendar day route name display
      }

      if (data.buildingManagers) {
        setBuildingManagers(data.buildingManagers);
      }
    } catch (error) {
      console.error("Error loading building manager calendar:", error);
      toast.error("Failed to load building manager calendar");
      
      // Set default data for testing
      setBuildingManager({
        id: buildingManagerId,
        fullName: `Building Manager ${buildingManagerId}`,
        email: `manager${buildingManagerId}@example.com`,
      });
      setPropertiesByRoute({});
      setRoutes({});
      setTypes({});
      setMonths([]);
      setRoutesNames({});
    } finally {
      loadDataInFlightRef.current = false;
      setLoading(false);
      // Refresh calendar events after data reload (covers all mutation handlers)
      // The in-flight guard on loadCalendarEvents prevents duplicates during initial mount
      if (loadCalendarEventsRef.current) {
        loadCalendarEventsRef.current(calendarDate);
      }
    }
  };

  // Load paginated table data from server
  const loadTableData = useCallback(async (page, limit, search, status) => {
    try {
      setTableLoading(true);
      const response = await inspectionsService.getBuildingManagerInspectionsPaginated(
        buildingManagerId,
        { page, limit, search, status }
      );
      const result = response.data ? response : response;
      if (result.data) {
        setTableData(result.data);
      }
      if (result.pagination) {
        setTablePagination(result.pagination);
      }
      if (result.statusOverview) {
        setStatusOverview(result.statusOverview);
      }
    } catch (error) {
      console.error("Error loading paginated table data:", error);
      setTableData([]);
      setTablePagination({ current_page: 1, total_pages: 1, total_records: 0, per_page: limit });
    } finally {
      setTableLoading(false);
    }
  }, [buildingManagerId]);

  // Load table data when page, entriesPerPage, statusFilter, or buildingManagerId changes
  useEffect(() => {
    loadTableData(currentPage, entriesPerPage, searchTerm, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, entriesPerPage, statusFilter, buildingManagerId]);

  // Debounced search for table
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      loadTableData(1, entriesPerPage, searchTerm, statusFilter);
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Load calendar events for a date range (optimized - only visible window)
  const loadCalendarEvents = useCallback(async (centerDate) => {
    if (loadCalendarEventsInFlightRef.current) return;
    loadCalendarEventsInFlightRef.current = true;
    loadCalendarEventsRef.current = loadCalendarEvents;
    try {
      const d = centerDate || new Date();
      // Compute range: 1st of previous month to last of next month (covers month view overflow)
      const startDate = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const endDate = new Date(d.getFullYear(), d.getMonth() + 2, 0);
      const startStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      const endStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

      const response = await inspectionsService.getBuildingManagerCalendarEvents(
        buildingManagerId,
        startStr,
        endStr
      );
      const events = response.data || response || [];
      setCalendarInspections(events);
    } catch (error) {
      console.error("Error loading calendar events:", error);
      setCalendarInspections([]);
    } finally {
      loadCalendarEventsInFlightRef.current = false;
    }
  }, [buildingManagerId]);

  // Load calendar events on initial mount and when buildingManagerId changes
  useEffect(() => {
    loadCalendarEvents(calendarDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingManagerId]);

  // Convert inspections to calendar events
  const calendarEvents = calendarInspections
    .filter(inspection => inspection.day && inspection.time_from)
    .map(inspection => {
      console.log('📅 [CALENDAR EVENT] Creating event for inspection:', inspection.id, 'day:', inspection.day);
      const day = inspection.day; // Format: "dd/mm/yyyy" (Laravel format)
      const timeFrom = inspection.time_from || '09:00';
      const timeTo = inspection.time_to || '10:00';

      // Parse dd/mm/yyyy format (Laravel format) - JavaScript can't parse this directly
      const [dayNum, monthNum, yearNum] = day.split('/').map(Number);

      // Parse time
      const [hoursFrom, minutesFrom] = timeFrom.split(':').map(Number);
      const [hoursTo, minutesTo] = timeTo.split(':').map(Number);

      // Create dates with correct parsing (month is 0-indexed in JavaScript)
      const start = new Date(yearNum, monthNum - 1, dayNum, hoursFrom, minutesFrom, 0, 0);
      const end = new Date(yearNum, monthNum - 1, dayNum, hoursTo, minutesTo, 0, 0);

      // Skip invalid dates
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        console.warn('⚠️ Invalid date for inspection:', inspection.id, day, timeFrom, timeTo);
        return null;
      }

      return {
        id: inspection.id,
        title: `${inspection.property?.name || 'Property'} - ${inspection.type?.name || 'Inspection'}`,
        start,
        end,
        resource: inspection
      };
    })
    .filter(event => event !== null); // Remove any invalid events

  // Log total calendar events
  console.log('📅 [CALENDAR] Total calendar events created:', calendarEvents.length);

  // Handle calendar event click
  const handleEventClick = (event) => {
    console.log('🖱️ [handleEventClick] Called with event:', event);
    console.log('🖱️ [handleEventClick] event.resource:', event.resource);
    setEditingInspection(event.resource);
    const propertyName = event.resource.property?.name || '';
    setPropertySearchValue(propertyName);
    setFormData({
      property_id: event.resource.property?.id || '',
      property_title: propertyName,
      building_manager_id: buildingManagerId,
      type_id: event.resource.type?.id || '',
      day: formatDateForInput(event.resource.day || ''),
      time_from: formatTimeForInput(event.resource.time_from || ''),
      time_to: formatTimeForInput(event.resource.time_to || ''),
      description: event.resource.description || '',
      updated_at: event.resource.updated_at || ''
    });
    console.log('🖱️ [handleEventClick] Setting showEditModal to true');
    setShowEditModal(true);
  };

  // Handle event drop (drag to reschedule)
  const handleEventDrop = useCallback(async ({ event, start, end }) => {
    console.log('📅 [EVENT DROP] Rescheduling inspection:', event.resource?.id);
    console.log('📅 [EVENT DROP] New start:', start, 'New end:', end);

    const inspectionId = event.resource?.id || event.id;
    if (!inspectionId) {
      toast.error('Could not identify inspection to reschedule');
      return;
    }

    // Format dates for backend (dd/mm/yyyy HH:mm format)
    const startStr = format(start, 'dd/MM/yyyy HH:mm');
    const endStr = format(end, 'dd/MM/yyyy HH:mm');

    try {
      // Call reschedule API with id, start, end parameters
      const response = await inspectionsService.rescheduleInspection(inspectionId, startStr, endStr);

      // Backend returns { message: "Inspection date has been changed!", model: ... }
      if (response.message?.includes('changed') || response.model) {
        toast.success(response.message || 'Inspection rescheduled successfully!');
        // Reload data to reflect changes
        loadData();
      } else {
        toast.error(response.message || 'Failed to reschedule inspection');
      }
    } catch (error) {
      console.error('Error rescheduling inspection:', error);
      toast.error(error.response?.data?.message || 'Failed to reschedule inspection');
    }
  }, [loadData]);

  // Handle calendar date selection
  const handleSelectSlot = async ({ start, end }) => {
    // If we have drag data, create inspection from drag & drop
    if (dragData) {
      const day = format(start, 'dd/MM/yyyy');
      const timeFrom = format(start, 'HH:mm');
      const timeTo = format(end || start, 'HH:mm');
      const startStr = `${day} ${timeFrom}`;
      const endStr = `${day} ${timeTo}`;

      const typeSelect = document.getElementById('calendar_inspection_type_id_to_add');
      const typeId = typeSelect ? typeSelect.value : Object.keys(types)[0] || '1';

      try {
        const response = await inspectionsService.addFromCalendar({
          type: dragData.type || 'property',
          start: startStr,
          end: endStr,
          id: dragData.routeId || dragData.propertyId,
          building_manager_id: buildingManagerId,
          type_id: typeId,
        });

        // Backend returns { message, result } - check for result to determine success
        if (response.result || response.message?.includes('assigned')) {
          toast.success(response.message || 'Inspection(s) created successfully!');
          loadData();
        } else {
          toast.error(response.message || 'Failed to create inspection');
        }
      } catch (error) {
        console.error('Error adding inspection from calendar:', error);
        toast.error(error.response?.data?.message || 'Failed to create inspection');
      }

      setDragData(null);
      return;
    }

    // Don't open modal on empty cell click - only used for drag & drop
    // User can use the properties panel on the right to create inspections
    console.log('📅 [SLOT SELECT] Empty slot clicked, no action (use drag & drop to create inspections)');
  };

  // Handle property search
  const handlePropertySearch = async (value) => {
    setPropertySearchValue(value);
    if (value.length >= 2) {
      try {
        const response = await inspectionsService.searchProperties(value);
        setPropertySearchResults(response.data || []);
        setShowPropertyTypeahead(true);
      } catch (error) {
        console.error('Error searching properties:', error);
        setPropertySearchResults([]);
      }
    } else {
      setPropertySearchResults([]);
      setShowPropertyTypeahead(false);
    }
  };

  // Handle property selection
  const handlePropertySelect = (property) => {
    setFormData({
      ...formData,
      property_id: property.id,
      property_title: property.name
    });
    setPropertySearchValue(property.name);
    setShowPropertyTypeahead(false);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle add inspection
  const handleAddInspection = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      await inspectionsService.createInspections({
        building_manager_id: formData.building_manager_id,
        property_id: formData.property_id,
        type_id: formData.type_id,
        day: convertDateForBackend(formData.day),
        time_from: formatTimeForInput(formData.time_from),
        time_to: formatTimeForInput(formData.time_to),
        description: formData.description
      });
      
      toast.success('Inspection created successfully!');
      setShowAddModal(false);
      setFormData({
        property_id: '',
        property_title: '',
        building_manager_id: buildingManagerId,
        type_id: '',
        day: '',
        time_from: '',
        time_to: '',
        description: '',
        updated_at: ''
      });
      setPropertySearchValue('');
      loadData();
    } catch (error) {
      console.error('Error creating inspection:', error);
      toast.error(error.response?.data?.message || 'Failed to create inspection');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle update inspection
  const handleUpdateInspection = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      await inspectionsService.updateInspection(editingInspection.id, {
        property_id: formData.property_id,
        building_manager_id: buildingManagerId,
        type_id: formData.type_id,
        day: convertDateForBackend(formData.day),
        time_from: formatTimeForInput(formData.time_from),
        time_to: formatTimeForInput(formData.time_to),
        description: formData.description
      });
      
      toast.success('Inspection updated successfully!');
      setShowEditModal(false);
      setEditingInspection(null);
      loadData(); // Reload data
    } catch (error) {
      console.error('Error updating inspection:', error);
      toast.error(error.response?.data?.message || 'Failed to update inspection');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle delete inspection
  const handleDeleteInspection = async () => {
    if (!window.confirm('Are you sure you want to delete this inspection?')) {
      return;
    }
    
    setFormLoading(true);
    
    try {
      await inspectionsService.deleteInspection(editingInspection.id);
      toast.success('Inspection deleted successfully!');
      setShowEditModal(false);
      setEditingInspection(null);
      loadData(); // Reload data
    } catch (error) {
      console.error('Error deleting inspection:', error);
      toast.error(error.response?.data?.message || 'Failed to delete inspection');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle edit button click from table
  const handleEditClick = (inspection) => {
    setEditingInspection(inspection);
    const propertyName = inspection.property?.name || '';
    setPropertySearchValue(propertyName);
    setFormData({
      property_id: inspection.property?.id || '',
      property_title: inspection.property?.name || '',
      building_manager_id: buildingManagerId,
      type_id: inspection.type?.id || '',
      day: formatDateForInput(inspection.day || ''),
      time_from: formatTimeForInput(inspection.time_from || ''),
      time_to: formatTimeForInput(inspection.time_to || ''),
      description: inspection.description || '',
      updated_at: inspection.updated_at || ''
    });
    setShowEditModal(true);
  };

  const handleSendReminderEmail = () => {
    navigate(`/inspections/building-manager/${buildingManagerId}/email-reminder/create`);
  };

  // Handle random scheduling
  const handleScheduleRandomly = async () => {
    const monthSelect = document.getElementById('random_month');
    const yearInput = document.getElementById('random_year');
    const errorDiv = document.getElementById('random_error');

    if (!monthSelect || !yearInput) return;

    const month = parseInt(monthSelect.value);
    const year = parseInt(yearInput.value);

    console.log('📅 [FRONTEND] Schedule randomly - month:', month, 'year:', year);

    if (!month || !year || year < 2000) {
      if (errorDiv) errorDiv.textContent = 'Please enter a valid month and year (e.g., 2025)';
      return;
    }

    if (errorDiv) errorDiv.textContent = 'Inspection will be scheduled (in the background)! Refresh the page soon to see the changes.';

    try {
      const response = await inspectionsService.scheduleRandomly(month, year, buildingManagerId);
      console.log('📅 [FRONTEND] Schedule randomly response:', response);

      // Handle different response messages
      if (response.message) {
        if (errorDiv) errorDiv.textContent = response.message;

        if (response.message.includes('almost done')) {
          // Success - inspections were scheduled
          toast.success(response.message);
          setTimeout(() => {
            loadData();
          }, 2000);
        } else if (response.message.includes('No properties found')) {
          // Informational - building manager has no properties
          toast.info(response.message);
        } else {
          // Other messages (errors)
          toast.error(response.message);
        }
      } else {
        if (errorDiv) errorDiv.textContent = 'An error occurred';
        toast.error('An error occurred');
      }
    } catch (error) {
      console.error('Error scheduling randomly:', error);
      if (errorDiv) errorDiv.textContent = 'Error scheduling inspections';
      toast.error('Failed to schedule inspections');
    }
  };

  // Handle calendar drop (from drag & drop) - create inspection directly like Laravel
  const handleCalendarDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const propertyId = e.dataTransfer.getData('propertyId');
    const routeId = e.dataTransfer.getData('routeId');
    const routeType = e.dataTransfer.getData('type');

    if (!propertyId && !routeId) {
      return;
    }

    // Try to get the date from the drop target (calendar cell)
    let dropDate = null;

    // Method 1: Check for data-date attribute on target or parents
    let targetEl = e.target;
    for (let i = 0; i < 10 && targetEl; i++) {
      // react-big-calendar uses different structures, check various attributes
      const dateAttr = targetEl.getAttribute('data-date') ||
                       targetEl.getAttribute('data-value');
      if (dateAttr) {
        dropDate = new Date(dateAttr);
        break;
      }

      // Check for role="cell" with aria-label containing date
      const ariaLabel = targetEl.getAttribute('aria-label');
      if (ariaLabel) {
        // Skip non-date aria-labels (navigation buttons, view labels, etc.)
        const datePattern = /^(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2}$/i;

        if (!datePattern.test(ariaLabel)) {
          console.log('📅 [CALENDAR DROP] Skipping non-date aria-label:', ariaLabel);
          targetEl = targetEl.parentElement;
          continue;
        }

        console.log('📅 [CALENDAR DROP] Found valid date aria-label:', ariaLabel);

        // Check if cell has off-range class (overflow day indicator) FIRST
        let isOffRange = false;
        let checkEl = targetEl;
        for (let j = 0; j < 5 && checkEl; j++) {
          if (checkEl.classList?.contains('rbc-off-range') || checkEl.classList?.contains('rbc-off-range-bg')) {
            isOffRange = true;
            break;
          }
          checkEl = checkEl.parentElement;
        }
        console.log('📅 [CALENDAR DROP] isOffRange:', isOffRange);

        // Parse the date using the calendar's current view year
        let parsed = new Date(ariaLabel + ' ' + calendarDate.getFullYear());
        console.log('📅 [CALENDAR DROP] Initial parsed date:', parsed);

        if (!isNaN(parsed.getTime())) {
          const parsedMonth = parsed.getMonth();
          const calendarMonth = calendarDate.getMonth();
          console.log('📅 [CALENDAR DROP] parsedMonth:', parsedMonth, 'calendarMonth:', calendarMonth);

          // ONLY adjust year if BOTH conditions are true:
          // 1. The cell is marked as off-range (rbc-off-range class)
          // 2. The parsed month differs from calendar month
          if (isOffRange && parsedMonth !== calendarMonth) {
            if (parsedMonth === 0 && calendarMonth === 11) {
              // January shown in December view -> next year
              parsed = new Date(calendarDate.getFullYear() + 1, parsedMonth, parsed.getDate());
              console.log('📅 [CALENDAR DROP] Adjusted overflow date to next year:', parsed);
            } else if (parsedMonth === 11 && calendarMonth === 0) {
              // December shown in January view -> previous year
              parsed = new Date(calendarDate.getFullYear() - 1, parsedMonth, parsed.getDate());
              console.log('📅 [CALENDAR DROP] Adjusted overflow date to previous year:', parsed);
            }
          }

          dropDate = parsed;
          break;
        }
      }

      // Check class for date cells in month view
      if (targetEl.classList?.contains('rbc-day-bg') || targetEl.classList?.contains('rbc-date-cell')) {
        // Try to find sibling or child with date info
        const dateHeader = targetEl.querySelector('.rbc-button-link') ||
                          targetEl.parentElement?.querySelector('.rbc-date-cell .rbc-button-link');
        if (dateHeader) {
          const dayNum = parseInt(dateHeader.textContent);
          if (!isNaN(dayNum)) {
            // Check if this is an overflow day (from previous/next month)
            // Find the corresponding date cell to check for rbc-off-range class
            let isOffRange = false;
            let checkEl = targetEl;
            for (let j = 0; j < 5 && checkEl; j++) {
              if (checkEl.classList?.contains('rbc-off-range') || checkEl.classList?.contains('rbc-off-range-bg')) {
                isOffRange = true;
                break;
              }
              checkEl = checkEl.parentElement;
            }

            let year = calendarDate.getFullYear();
            let month = calendarDate.getMonth();

            if (isOffRange) {
              // If day is low (1-14), it's from next month
              // If day is high (15-31), it's from previous month
              if (dayNum <= 14) {
                month = month + 1;
                if (month > 11) {
                  month = 0;
                  year = year + 1;
                }
              } else {
                month = month - 1;
                if (month < 0) {
                  month = 11;
                  year = year - 1;
                }
              }
              console.log('📅 [CALENDAR DROP] Detected overflow day:', { dayNum, isOffRange, adjustedMonth: month + 1, adjustedYear: year });
            }

            dropDate = new Date(year, month, dayNum);
            break;
          }
        }
      }

      targetEl = targetEl.parentElement;
    }

    // Method 2: Find all day cells and match by position
    if (!dropDate || isNaN(dropDate.getTime())) {
      const allDayCells = document.querySelectorAll('.rbc-day-bg');
      const dropX = e.clientX;
      const dropY = e.clientY;

      for (const cell of allDayCells) {
        const rect = cell.getBoundingClientRect();
        if (dropX >= rect.left && dropX <= rect.right &&
            dropY >= rect.top && dropY <= rect.bottom) {
          // Found the cell, now find the corresponding date header
          const cellIndex = Array.from(allDayCells).indexOf(cell);
          const dateHeaders = document.querySelectorAll('.rbc-date-cell');
          if (dateHeaders[cellIndex]) {
            const dateCell = dateHeaders[cellIndex];
            const dayNum = parseInt(dateCell.textContent);
            if (!isNaN(dayNum)) {
              // Check if this is an overflow day (from previous/next month)
              const isOffRange = dateCell.classList?.contains('rbc-off-range') ||
                                 cell.classList?.contains('rbc-off-range-bg');

              let year = calendarDate.getFullYear();
              let month = calendarDate.getMonth();

              if (isOffRange) {
                // If day is low (1-14), it's from next month
                // If day is high (15-31), it's from previous month
                if (dayNum <= 14) {
                  month = month + 1;
                  if (month > 11) {
                    month = 0;
                    year = year + 1;
                  }
                } else {
                  month = month - 1;
                  if (month < 0) {
                    month = 11;
                    year = year - 1;
                  }
                }
                console.log('📅 [CALENDAR DROP] Method 2 - Detected overflow day:', { dayNum, isOffRange, adjustedMonth: month + 1, adjustedYear: year });
              }

              dropDate = new Date(year, month, dayNum);
              console.log('📅 [CALENDAR DROP] Found date via cell position:', dropDate);
              break;
            }
          }
        }
      }
    }

    // If we still couldn't detect the date, fall back to the two-step flow
    if (!dropDate || isNaN(dropDate.getTime())) {
      console.log('📅 [CALENDAR DROP] Could not detect date from drop target, using two-step flow');
      setDragData({
        propertyId,
        routeId,
        type: routeType || 'property',
      });
      toast.info('Click on a date in the calendar to create the inspection', {
        autoClose: 3000,
      });
      return;
    }

    console.log('📅 [CALENDAR DROP] Detected drop date:', dropDate);
    console.log('📅 [CALENDAR DROP] Calendar date state:', calendarDate);
    console.log('📅 [CALENDAR DROP] Drop date details:', {
      year: dropDate.getFullYear(),
      month: dropDate.getMonth() + 1,
      day: dropDate.getDate(),
      toString: dropDate.toString(),
      toISOString: dropDate.toISOString()
    });

    // Use default time (09:00-10:00) like Laravel
    // Laravel format: DD/MM/YYYY HH:mm (uppercase DD and YYYY)
    // date-fns format: dd = day with leading zero, yyyy = 4-digit year
    const formattedDay = format(dropDate, 'dd/MM/yyyy'); // Produces "01/01/2025"
    const startStr = `${formattedDay} 09:00`; // Produces "01/01/2025 09:00"
    const endStr = `${formattedDay} 10:00`; // Produces "01/01/2025 10:00"

    // Get type_id from dropdown or default to first available type
    const typeSelect = document.getElementById('calendar_inspection_type_id_to_add');
    let typeId = typeSelect ? typeSelect.value : null;

    // If no type selected, use first available type from types object
    if (!typeId && Object.keys(types).length > 0) {
      typeId = Object.keys(types)[0];
    }

    // Fallback to '1' if no types available
    if (!typeId) {
      typeId = '1';
      console.warn('⚠️ [CALENDAR DROP] No type_id found, using default: 1');
    }

    // Ensure typeId is a string (backend expects string that will be parsed)
    typeId = String(typeId);

    console.log('📅 [CALENDAR DROP] Sending to backend:', {
      type: routeType || 'property',
      start: startStr,
      end: endStr,
      id: routeId || propertyId,
      building_manager_id: buildingManagerId,
      type_id: typeId,
      dropDate: dropDate.toISOString(),
      dropDateLocal: dropDate.toString()
    });

    // Validate required fields before sending
    if (!buildingManagerId) {
      toast.error('Building manager ID is missing');
      console.error('❌ [CALENDAR DROP] Missing building_manager_id');
      return;
    }

    if (!propertyId && !routeId) {
      toast.error('Property or Route ID is required');
      console.error('❌ [CALENDAR DROP] Missing property_id or route_id');
      return;
    }

    if (!typeId) {
      toast.error('Inspection type is required');
      console.error('❌ [CALENDAR DROP] Missing type_id');
      return;
    }

    try {
      const requestData = {
        type: routeType || 'property',
        start: startStr,
        end: endStr,
        id: routeId || propertyId,
        building_manager_id: buildingManagerId,
        type_id: typeId,
      };

      console.log('📅 [CALENDAR DROP] Full request data:', requestData);

      const response = await inspectionsService.addFromCalendar(requestData);

      // Backend returns { message, result } - check for result to determine success
      if (response.result || response.message?.includes('assigned')) {
        toast.success(response.message || 'Inspection created successfully!');
        loadData();
      } else {
        toast.error(response.message || 'Failed to create inspection');
      }
    } catch (error) {
      console.error('Error adding inspection from calendar:', error);
      toast.error(error.response?.data?.message || 'Failed to create inspection');
    }
  };

  // Handle trash drop (delete inspection)
  const handleTrashDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    console.log('🗑️ [TRASH DROP] Drop event triggered');
    console.log('🗑️ [TRASH DROP] DataTransfer types:', e.dataTransfer.types);

    // Try to get inspectionId from dataTransfer (for HTML5 drag)
    let inspectionId = e.dataTransfer.getData('inspectionId');
    console.log('🗑️ [TRASH DROP] inspectionId from dataTransfer:', inspectionId);

    // Check if this is a property drag (not an inspection)
    const propertyId = e.dataTransfer.getData('propertyId');
    const routeId = e.dataTransfer.getData('routeId');

    if (!inspectionId && (propertyId || routeId)) {
      // User dragged a property/route to trash, not an inspection
      console.log('ℹ️ [TRASH DROP] Property/route dropped, not an inspection');
      toast.info('To delete an inspection, drag a scheduled event from the calendar to the trash area.', {
        autoClose: 4000,
      });
      return;
    }

    // If not found, try to get it from the ref (for react-big-calendar events)
    if (!inspectionId && draggedInspectionIdRef.current) {
      inspectionId = draggedInspectionIdRef.current;
      console.log('🗑️ [TRASH DROP] inspectionId from ref:', inspectionId);
    }

    // If still not found, try to match from text/plain
    if (!inspectionId) {
      const draggedEvent = e.dataTransfer.getData('text/plain');
      console.log('🗑️ [TRASH DROP] draggedEvent text:', draggedEvent);

      if (draggedEvent) {
        // Find the inspection by matching the event title
        const matchingInspection = calendarInspections.find(insp => {
          const title = `${insp.property?.name || 'Property'} - ${insp.type?.name || 'Inspection'}`;
          return title === draggedEvent;
        });
        if (matchingInspection) {
          inspectionId = matchingInspection.id;
          console.log('🗑️ [TRASH DROP] Found matching inspection:', inspectionId);
        }
      }
    }

    if (!inspectionId) {
      console.log('❌ [TRASH DROP] No inspectionId found in drop data');
      toast.warning('Could not identify inspection to delete. Drag a scheduled inspection from the calendar.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this inspection?')) {
      draggedInspectionIdRef.current = null;
      return;
    }

    try {
      console.log('🗑️ [TRASH DROP] Deleting inspection:', inspectionId);
      await inspectionsService.deleteInspection(inspectionId);
      toast.success('Inspection deleted successfully!');
      draggedInspectionIdRef.current = null;
      loadData();
    } catch (error) {
      console.error('❌ [TRASH DROP] Error deleting inspection:', error);
      toast.error(error.response?.data?.message || 'Failed to delete inspection');
      draggedInspectionIdRef.current = null;
    }
  };


  // Handle building manager filter
  const handleBuildingManagerFilter = (e) => {
    setFilteredBuildingManagerId(e.target.value);
  };

  // Helper function to check if property should be visible based on filter
  const isPropertyVisible = (property) => {
    if (filteredBuildingManagerId === '0') {
      return true; // Show all
    } else if (filteredBuildingManagerId === '-1') {
      return !property.building_manager_id || property.building_manager_id === ''; // Show unassigned
    } else {
      return String(property.building_manager_id) === filteredBuildingManagerId; // Show selected building manager
    }
  };

  // Custom day cell renderer to show route names (Laravel pattern)
  const dayPropGetter = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const routeName = routesNames[dateStr] || '';
    
    return {
      style: {
        position: 'relative',
      },
      title: routeName ? `Route: ${routeName}` : '',
      className: routeName ? 'has-route' : '',
    };
  };

  // Custom day header formatter to show route names
  const formats = {
    dayHeaderFormat: (date, culture, localizer) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const routeName = routesNames[dateStr];
      const dayName = localizer.format(date, 'ddd M/d', culture);
      return routeName ? `${dayName} - ${routeName}` : dayName;
    },
  };



  if (loading) {
    return (
      <div className="container">
        <div className="panel">
          <div className="panel-body">
            <p>Loading building manager calendar...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.mainContainer}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumbContainer}>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/dashboard">Dashboard</a>
            </li>
            <li className="breadcrumb-item">
              <a href="/inspections">Inspections</a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {buildingManager?.fullName || "Building Manager"} Calendar
            </li>
          </ol>
        </nav>
      </div>

      {/* Send Reminder Email Button */}
      <div className={styles.buttonContainer}>
        <div className="col-md-12 hidden-print">
          <button
            className="btn btn-head"
            onClick={handleSendReminderEmail}
          >
            Send reminder email
          </button>
        </div>
      </div>

      {/* Main Calendar Panel */}
      <div className={styles.calendarSection}>
        <div className={`row ${styles.calendarRow}`}>
          <div className={`col-md-8 ${styles.leftColumn}`}>
          {/* Random Scheduling Panel */}
          <div className={`panel panel-default panel-standard ${styles.randomPanel}`}>
            <div className="panel-heading panel-title">Random</div>
            <div className="panel-body">
              <div className="row">
                <div className="col-sm-3">
                  <select
                    id="random_month"
                    className="form-control"
                    defaultValue={new Date().getMonth() + 1}
                  >
                    {months.map((month) => (
                      <option key={month.id} value={month.value.split('-')[1]}>
                        {month.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-2">
                  <input
                    type="text"
                    id="random_year"
                    className="form-control"
                    defaultValue={new Date().getFullYear()}
                    style={{ minWidth: '70px' }}
                  />
                </div>
                <div className="col-sm-5">
                  <button
                    id="schedule_randomly_monthly_inspections"
                    className="btn-head"
                    type="button"
                    onClick={handleScheduleRandomly}
                  >
                    Schedule randomly monthly inspections
                  </button>
                  <div id="random_error" className="inline-block text-danger" style={{ marginLeft: '10px' }}></div>
                </div>
              </div>
            </div>
          </div>

          <div className="panel panel-default panel-standard">
            {/* Hidden route names for each day (Laravel pattern) */}
            <div style={{ display: 'none' }}>
              {Object.entries(routesNames).map(([day, routeName]) => (
                <div key={day} id={`route-day-${day}`}>
                  {routeName}
                </div>
              ))}
            </div>
            
            <div className="panel-heading panel-title">
              <span>
                <strong>{buildingManager?.fullName || "Building Manager"}</strong> inspections
              </span>
            </div>
            <div className="panel-body">
              <div
                id="inspections-calendar-building-manager"
                className={styles.calendarWrapper}
                onDrop={handleCalendarDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <DragAndDropCalendar
                  localizer={localizer}
                  events={calendarEvents}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: '100%' }}
                  date={calendarDate}
                  onNavigate={(date) => {
                    setCalendarDate(date);
                    loadCalendarEvents(date);
                  }}
                  onSelectEvent={(event) => {
                    console.log('📅 [CALENDAR] onSelectEvent triggered:', event);
                    handleEventClick(event);
                  }}
                  onEventDrop={handleEventDrop}
                  onEventResize={handleEventDrop}
                  draggableAccessor={() => true}
                  resizable
                  selectable
                  onSelectSlot={handleSelectSlot}
                  defaultView="month"
                  views={['month', 'week', 'day', 'agenda']}
                  min={new Date(1970, 0, 1, 7, 0, 0)} // minTime: '07:00'
                  max={new Date(1970, 0, 1, 23, 59, 59)} // maxTime: '23:59'
                  step={60} // 1 hour steps
                  timeslots={1}
                  components={{
                    month: {
                      dateHeader: ({ date, label }) => {
                        const dateStr = format(date, 'yyyy-MM-dd');
                        const routeName = routesNames[dateStr];
                        return (
                          <div className={styles.routeDay}>
                            <span>{label}</span>
                            {routeName && (
                              <div className={styles.routeDayLabel}>
                                {routeName}
                              </div>
                            )}
                          </div>
                        );
                      },
                    },
                  }}
                  formats={{
                    ...formats,
                    timeGutterFormat: 'HH:mm',
                    eventTimeRangeFormat: ({ start, end }) =>
                      `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`,
                  }}
                  dayPropGetter={dayPropGetter}
                  popup
                  eventPropGetter={(event) => ({
                    style: {
                      cursor: 'grab',
                    },
                  })}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Hand Column */}
        <div className={`col-md-4 ${styles.rightColumn}`}>
          {/* Trash Area */}
          <div
            id="calendarTrash"
            className={`panel panel-default panel-standard js-trash-inspection ${styles.trashArea}`}
            onDrop={handleTrashDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <div className="panel-heading panel-title">
              <i className="fa fa-trash" aria-hidden="true"></i>
            </div>
            <div className="panel-body">
              Drop an inspection here to remove it completely
            </div>
          </div>

          {/* Manage Inspections Panel */}
          <div className="panel panel-default panel-standard">
            <div className="panel-heading panel-title">Descriptions of statuses</div>
            <div className="panel-body">
              <div>
                <span><strong>Filter by Building Manager: </strong></span>
                <select
                  id="calendar_bm_filter_bm_select"
                  className="form-control"
                  value={filteredBuildingManagerId}
                  onChange={handleBuildingManagerFilter}
                >
                  <option value="0">All</option>
                  <option value="-1">Unassigned</option>
                  {Object.entries(buildingManagers).map(([id, name]) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-20">
                <span><strong>Type for created inspection (by drag & drop): </strong></span>
                <select
                  id="calendar_inspection_type_id_to_add"
                  className="form-control"
                  defaultValue={Object.keys(types)[0] || ''}
                >
                  {Object.entries(types).map(([id, name]) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Collapsible Status Descriptions Panel (matching Laravel) */}
              <div className="card mt-3">
                <div
                  className="card-header d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setStatusDescriptionCollapsed(!statusDescriptionCollapsed)}
                >
                  <span>Descriptions of statuses</span>
                  <i className={`fa fa-arrow-${statusDescriptionCollapsed ? 'down' : 'up'}`} aria-hidden="true"></i>
                </div>
                {!statusDescriptionCollapsed && (
                  <div className="card-body">
                    <div className="mb-2">
                      <span className="badge me-2" style={{ backgroundColor: 'blue', color: 'white' }}>
                        <strong>To be Scheduled</strong>
                      </span>
                      - there is no inspection for this month
                    </div>
                    <div className="mb-2">
                      <span className="badge me-2" style={{ backgroundColor: 'burlywood', color: 'black' }}>
                        <strong>Inspection Scheduled</strong>
                      </span>
                      - monthly inspection was created and is upcoming
                    </div>
                    <div className="mb-2">
                      <span className="badge me-2" style={{ backgroundColor: 'green', color: 'white' }}>
                        <strong>Completed</strong>
                      </span>
                      - monthly inspection was done
                    </div>
                    <div className="mb-2">
                      <span className="badge me-2" style={{ backgroundColor: 'orange', color: 'black' }}>
                        <strong>To be published</strong>
                      </span>
                      - report is not published
                    </div>
                    <div className="mb-2">
                      <span className="badge me-2" style={{ backgroundColor: 'purple', color: 'white' }}>
                        <strong>Past Inspection To Complete</strong>
                      </span>
                      - all Monthly Inspection Reports are added
                    </div>
                    <div className="mb-2">
                      <span className="badge me-2" style={{ backgroundColor: 'red', color: 'white' }}>
                        <strong>Overdue</strong>
                      </span>
                      - lack of at least one published Monthly Inspection Report
                    </div>
                  </div>
                )}
              </div>

              <hr />

              <div id="filter-wrapper" className={styles.filterWrapper}>
                {/* Properties grouped by route */}
                {Object.entries(routes).map(([routeId, routeName]) => (
                  <div key={routeId} className="event-in-filter-in-group" data-route={routeId}>
                    <div
                      className={`event-in-filter-grouped draggable-route draggable-element ${styles.routeItem}`}
                      id={`route-${routeId}`}
                      data-id={routeId}
                      data-type="route"
                      onDragStart={(e) => {
                        e.dataTransfer.setData('routeId', routeId);
                        e.dataTransfer.setData('type', 'route');
                        setDragData({
                          routeId: routeId,
                          type: 'route',
                        });
                      }}
                      draggable
                    >
                      {routeName}
                    </div>

                    {propertiesByRoute[routeId] && propertiesByRoute[routeId].filter(isPropertyVisible).map((property) => {
                      const bgColor = getPropertyBackgroundColor(property);
                      return (
                        <div
                          key={property.id}
                          className={`event-in-filter draggable-element ${styles.propertyItem}`}
                          id={`property-${property.id}`}
                          data-id={property.id}
                          data-type="property"
                          style={{
                            backgroundColor: bgColor,
                            color: getTextColorForBackground(bgColor)
                          }}
                        onDragStart={(e) => {
                          e.dataTransfer.setData('propertyId', property.id);
                          e.dataTransfer.setData('propertyName', property.name);
                          e.dataTransfer.setData('type', 'property');
                          setDragData({
                            propertyId: property.id,
                            type: 'property',
                          });
                        }}
                          data-building-manager-id={property.building_manager_id || ''}
                          draggable
                        >
                          {property.property_id && property.property_id.length > 10
                            ? `${property.property_id.substring(0, 9)}...`
                            : property.property_id || property.id}
                          {' - '}
                          {property.name}
                        </div>
                      );
                    })}
                  </div>
                ))}

                {/* Properties with no route */}
                {propertiesByRoute[0] && propertiesByRoute[0].length > 0 && (
                  <div className="event-in-filter-in-group" data-route="0">
                    <div className={`event-in-filter-grouped ${styles.noRouteHeader}`}>
                      No route
                    </div>

                    {propertiesByRoute[0].filter(isPropertyVisible).map((property) => {
                      const bgColor = property.monthly_inspection_status !== undefined ? getPropertyBackgroundColor(property) : 'blue';
                      return (
                        <div
                          key={property.id}
                          className={`event-in-filter draggable-element ${styles.propertyItem}`}
                          id={`property-${property.id}`}
                          data-id={property.id}
                          data-type="property"
                          style={{
                            backgroundColor: bgColor,
                            color: getTextColorForBackground(bgColor)
                          }}
                        onDragStart={(e) => {
                          e.dataTransfer.setData('propertyId', property.id);
                          e.dataTransfer.setData('propertyName', property.name);
                          e.dataTransfer.setData('type', 'property');
                          setDragData({
                            propertyId: property.id,
                            type: 'property',
                          });
                        }}
                          data-building-manager-id={property.building_manager_id || ''}
                          draggable
                        >
                          {property.property_id && property.property_id.length > 10
                            ? `${property.property_id.substring(0, 9)}...`
                            : property.property_id || property.id}
                          {' - '}
                          {property.name}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Status Overview - Clickable filters like Laravel */}
      <div className={styles.statusOverviewSection}>
        <div className="col-md-12">
          <div className="panel-title title-standard">
            <h3>Status overview</h3>
          </div>
          <div className="panel-group mb-20 border-down">
            <div className="col-md-2">
              <div
                className={`activity-box js-inspections-overview-box ${statusFilter === 'all' ? 'active' : ''}`}
                style={{
                  backgroundColor: "#5bc0de",
                  cursor: 'pointer',
                  opacity: statusFilter === 'all' ? 1 : 0.7,
                  border: statusFilter === 'all' ? '3px solid #333' : 'none'
                }}
                onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
                data-status_id=""
              >
                <p className="activity-value">{statusOverview.all}</p>
                <p className="activity-title">ALL</p>
              </div>
            </div>
            <div className="col-md-2">
              <div
                className={`activity-box js-inspections-overview-box ${statusFilter === 'upcoming' ? 'active' : ''}`}
                style={{
                  backgroundColor: "green",
                  cursor: 'pointer',
                  opacity: statusFilter === 'upcoming' ? 1 : 0.7,
                  border: statusFilter === 'upcoming' ? '3px solid #333' : 'none'
                }}
                onClick={() => { setStatusFilter('upcoming'); setCurrentPage(1); }}
                data-status_id="upcoming"
              >
                <p className="activity-value">{statusOverview.upcoming}</p>
                <p className="activity-title">UPCOMING</p>
              </div>
            </div>
            <div className="col-md-2">
              <div
                className={`activity-box js-inspections-overview-box ${statusFilter === 'past' ? 'active' : ''}`}
                style={{
                  backgroundColor: "red",
                  cursor: 'pointer',
                  opacity: statusFilter === 'past' ? 1 : 0.7,
                  border: statusFilter === 'past' ? '3px solid #333' : 'none'
                }}
                onClick={() => { setStatusFilter('past'); setCurrentPage(1); }}
                data-status_id="past"
              >
                <p className="activity-value">{statusOverview.past}</p>
                <p className="activity-title">PAST</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inspections DataTable */}
      <div className={styles.inspectionsTableSection}>
        <div className="col-md-12">
          <div className="panel panel-default panel-standard">
            <div className="panel-heading panel-title">
              <h3>List</h3>
            </div>
            <div className="panel-body">
              {tablePagination.total_records > 0 || tableData.length > 0 || searchTerm || statusFilter !== 'all' ? (
                <>
                  {/* Search and Controls */}
                  <div className="row" style={{ marginBottom: '15px' }}>
                    <div className="col-md-6">
                      <div className="dataTables_filter">
                        <label>
                          Search:
                          <input
                            type="search"
                            className="form-control input-sm"
                            placeholder="Search inspections..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ marginLeft: '10px', display: 'inline-block', width: 'auto' }}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6 text-right">
                      <label>
                        Show{' '}
                        <select
                          className="form-control input-sm"
                          value={entriesPerPage}
                          onChange={(e) => {
                            setEntriesPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                          style={{ display: 'inline-block', width: 'auto', margin: '0 5px' }}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>{' '}
                        entries
                      </label>
                    </div>
                  </div>

                  {/* DataTable */}
                  <div className="dataTable_wrapper">
                    <table className="table table-hover dt-responsive">
                      <thead>
                        <tr>
                          <th>Property</th>
                          <th>ID</th>
                          <th>Postcode</th>
                          <th>Day</th>
                          <th className="js-tooltip" title="Time From">From</th>
                          <th className="js-tooltip" title="Time To">To</th>
                          <th>Type</th>
                          <th>Calendar</th>
                          <th className="action-head">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableLoading ? (
                          <tr>
                            <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                              Loading...
                            </td>
                          </tr>
                        ) : tableData.length > 0 ? (
                          tableData.map((inspection) => (
                            <tr key={inspection.id}>
                              <td>
                                {inspection.property?.id ? (
                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      navigate(`/properties/${inspection.property.id}`);
                                    }}
                                    style={{ color: '#007bff', textDecoration: 'none', cursor: 'pointer' }}
                                  >
                                    {inspection.property.name || "-"}
                                  </a>
                                ) : "-"}
                              </td>
                              <td>{inspection.property?.property_id || inspection.property?.id || "-"}</td>
                              <td>{inspection.property?.postcode || "-"}</td>
                              <td>{inspection.day || "-"}</td>
                              <td>{inspection.time_from || "-"}</td>
                              <td>{inspection.time_to || "-"}</td>
                              <td>{inspection.type?.name || "-"}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => {
                                    navigate(`/inspections/property/${inspection.property?.id}`);
                                  }}
                                >
                                  View
                                </button>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-warning"
                                  onClick={() => navigate(`/inspections/${inspection.id}/edit`)}
                                  style={{ marginRight: '5px' }}
                                >
                                  Edit
                                </button>
                                {inspection.property?.id && (
                                  <button
                                    className="btn btn-sm btn-info"
                                    onClick={() => navigate(`/properties/${inspection.property.id}/monthly-inspections`)}
                                    style={{ marginRight: '5px' }}
                                  >
                                    Monthly Inspections
                                  </button>
                                )}
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={async () => {
                                    if (window.confirm('Are you sure you want to delete this inspection?')) {
                                      try {
                                        await inspectionsService.deleteInspection(inspection.id);
                                        toast.success('Inspection deleted successfully!');
                                        loadData();
                                        loadTableData(currentPage, entriesPerPage, searchTerm, statusFilter);
                                      } catch (error) {
                                        console.error('Error deleting inspection:', error);
                                        toast.error(error.response?.data?.message || 'Failed to delete inspection');
                                      }
                                    }
                                  }}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                              No inspections found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    <div className="row">
                      <div className="col-md-12">
                        <div className="pagination-info">
                          <div className="pagination-left">
                            Showing {tablePagination.total_records > 0 ? ((currentPage - 1) * entriesPerPage) + 1 : 0} to{' '}
                            {Math.min(currentPage * entriesPerPage, tablePagination.total_records)} of{' '}
                            {tablePagination.total_records} entries
                          </div>
                          <div className="pagination-right">
                            <nav>
                              <ul className="pagination">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                  >
                                    Previous
                                  </button>
                                </li>
                                {Array.from(
                                  { length: tablePagination.total_pages },
                                  (_, i) => i + 1
                                )
                                  .filter(
                                    (page) => {
                                      return (
                                        page === 1 ||
                                        page === tablePagination.total_pages ||
                                        (page >= currentPage - 2 && page <= currentPage + 2)
                                      );
                                    }
                                  )
                                  .map((page, index, array) => (
                                    <React.Fragment key={page}>
                                      {index > 0 && array[index - 1] !== page - 1 && (
                                        <li className="page-item disabled">
                                          <span className="page-link">...</span>
                                        </li>
                                      )}
                                      <li className={`page-item ${page === currentPage ? 'active' : ''}`}>
                                        <button
                                          className="page-link"
                                          onClick={() => setCurrentPage(page)}
                                        >
                                          {page}
                                        </button>
                                      </li>
                                    </React.Fragment>
                                  ))}
                                <li className={`page-item ${
                                  currentPage >= tablePagination.total_pages ? 'disabled' : ''
                                }`}>
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage >= tablePagination.total_pages}
                                  >
                                    Next
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <p>No inspections found for this building manager.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Inspection Modal */}
      {showAddModal && (
        <div className="modal fade show d-block" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, overflow: 'auto' }}>
          <div className="modal-dialog" style={{ position: 'relative', zIndex: 1051 }}>
            <div className="modal-content">
              <div className="modal-header">
                <button
                  type="button"
                  className="close btn-modal-close"
                  onClick={() => setShowAddModal(false)}
                >
                  &times;
                </button>
                <h4 className="modal-title">Add inspection</h4>
              </div>
              <div className="modal-body">
                <form className="modal-form" onSubmit={handleAddInspection}>
                  <fieldset className="form-horizontal">
                    {/* Property Field */}
                    <div className="form-group">
                      <label className="control-label col-sm-3">Property</label>
                      <div className="col-sm-9">
                        <div style={{ position: 'relative' }}>
                          <input
                            className="typeahead form-control js-property-field-typeahead"
                            id="property_title"
                            name="property_title"
                            type="text"
                            placeholder="Type property ID or name (or *)"
                            value={propertySearchValue}
                            onChange={(e) => handlePropertySearch(e.target.value)}
                            autoComplete="off"
                          />
                          <input
                            className="js-property-id-field"
                            name="property_id"
                            type="hidden"
                            value={formData.property_id}
                          />
                          {showPropertyTypeahead && propertySearchResults.length > 0 && (
                            <div className={styles.typeaheadDropdown}>
                              {propertySearchResults.map((property) => (
                                <div
                                  key={property.id}
                                  onClick={() => handlePropertySelect(property)}
                                  className={styles.typeaheadItem}
                                >
                                  {property.property_id || property.id} - {property.name}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Building Manager Field (Fixed) */}
                    <div className="form-group">
                      <label className="control-label col-sm-3 label-required">BM</label>
                      <div className="col-sm-5">
                        <input
                          type="hidden"
                          name="building_manager_id"
                          value={formData.building_manager_id}
                        />
                        <div className={styles.asInput}>
                          {buildingManager?.fullName || 'Building Manager'}
                        </div>
                      </div>
                    </div>

                    {/* Type Field */}
                    <div className="form-group">
                      <label className="control-label col-sm-3 label-required">Type</label>
                      <div className="col-sm-5">
                        <select
                          name="type_id"
                          className="form-control field-type"
                          required
                          value={formData.type_id}
                          onChange={handleInputChange}
                        >
                          <option value="">Please select</option>
                          {Object.entries(types).map(([id, name]) => (
                            <option key={id} value={id}>
                              {name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Day Field */}
                    <div className="form-group">
                      <label className="control-label col-sm-3 label-required">Day</label>
                      <div className="col-sm-5">
                        <input
                          type="text"
                          name="day"
                          className="form-control js-datepicker"
                          placeholder="mm/dd/yyyy"
                          required
                          value={formData.day}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Time From Field */}
                    <div className="form-group">
                      <label className="control-label col-sm-3 label-required">Time From</label>
                      <div className="col-sm-5">
                        <input
                          type="text"
                          name="time_from"
                          className="form-control js-timepicker"
                          placeholder="HH:mm"
                          required
                          value={formData.time_from}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Time To Field */}
                    <div className="form-group">
                      <label className="control-label col-sm-3 label-required">Time To</label>
                      <div className="col-sm-5">
                        <input
                          type="text"
                          name="time_to"
                          className="form-control js-timepicker"
                          placeholder="HH:mm"
                          required
                          value={formData.time_to}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Description Field */}
                    <div className="form-group">
                      <label className="control-label col-sm-3">Description</label>
                      <div className="col-sm-9">
                        <textarea
                          name="description"
                          className="form-control"
                          rows="3"
                          value={formData.description}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </fieldset>

                  <div className="form-group text-right">
                    <button
                      type="submit"
                      className="btn btn-head btn-standard modal-submit-button"
                      disabled={formLoading}
                    >
                      {formLoading ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-default btn-modal-close"
                  onClick={() => {
                    setShowAddModal(false);
                    setPropertySearchValue('');
                    setShowPropertyTypeahead(false);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Inspection Modal - DISABLED FOR NOW */}
      {false && showEditModal && editingInspection && (
        <div className="modal fade show d-block" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, overflow: 'auto' }}>
          <div className="modal-dialog" style={{ position: 'relative', zIndex: 1051, marginTop: '50px' }}>
            <div className="modal-content" style={{ borderRadius: '0' }}>
              <div className="modal-header" style={{ backgroundColor: '#5bc0de', color: 'white', padding: '10px 15px', borderRadius: '0' }}>
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingInspection(null);
                    setPropertySearchValue('');
                    setShowPropertyTypeahead(false);
                  }}
                  style={{ color: 'white', opacity: 1, fontSize: '24px', fontWeight: 'normal', marginTop: '-2px' }}
                >
                  &times;
                </button>
                <h4 className="modal-title" style={{ margin: 0, fontSize: '16px', fontWeight: 'normal' }}>Edit inspection</h4>
              </div>
              <div className="modal-body" style={{ padding: '15px' }}>
                <form className="modal-form" onSubmit={handleUpdateInspection}>
                  <input type="hidden" name="id" value={editingInspection.id} />

                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                    <tbody>
                      {/* Property Field */}
                      <tr>
                        <td style={{ width: '100px', textAlign: 'right', paddingRight: '10px', verticalAlign: 'top' }}>
                          <label style={{ fontWeight: 'normal' }}>Property</label>
                        </td>
                        <td>
                          <div style={{ position: 'relative' }}>
                            <input
                              className="form-control"
                              type="text"
                              placeholder="Type property ID or name (or *)"
                              value={propertySearchValue}
                              onChange={(e) => handlePropertySearch(e.target.value)}
                              autoComplete="off"
                              style={{ width: '100%' }}
                            />
                            <input type="hidden" name="property_id" value={formData.property_id} />
                            {showPropertyTypeahead && propertySearchResults.length > 0 && (
                              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ccc', maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}>
                                {propertySearchResults.map((property) => (
                                  <div
                                    key={property.id}
                                    onClick={() => handlePropertySelect(property)}
                                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                  >
                                    {property.property_id || property.id} - {property.name}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Building Manager Field */}
                      <tr>
                        <td style={{ textAlign: 'right', paddingRight: '10px', verticalAlign: 'middle' }}>
                          <label style={{ fontWeight: 'normal' }}>BM <span style={{ color: 'red' }}>*</span></label>
                        </td>
                        <td>
                          <input type="hidden" name="building_manager_id" value={formData.building_manager_id} />
                          <span style={{ display: 'inline-block', padding: '6px 0' }}>{buildingManager?.fullName || 'Building Manager'}</span>
                        </td>
                      </tr>

                      {/* Type Field */}
                      <tr>
                        <td style={{ textAlign: 'right', paddingRight: '10px', verticalAlign: 'middle' }}>
                          <label style={{ fontWeight: 'normal' }}>Type <span style={{ color: 'red' }}>*</span></label>
                        </td>
                        <td>
                          <select
                            name="type_id"
                            className="form-control"
                            required
                            value={formData.type_id}
                            onChange={handleInputChange}
                            style={{ width: 'auto', minWidth: '200px' }}
                          >
                            <option value="">Please select</option>
                            {Object.entries(types).map(([id, name]) => (
                              <option key={id} value={id}>{name}</option>
                            ))}
                          </select>
                        </td>
                      </tr>

                      {/* Day Field */}
                      <tr>
                        <td style={{ textAlign: 'right', paddingRight: '10px', verticalAlign: 'middle' }}>
                          <label style={{ fontWeight: 'normal' }}>Day <span style={{ color: 'red' }}>*</span></label>
                        </td>
                        <td>
                          <input
                            type="date"
                            name="day"
                            className="form-control"
                            required
                            value={formData.day}
                            onChange={handleInputChange}
                            style={{ width: 'auto', minWidth: '150px' }}
                          />
                        </td>
                      </tr>

                      {/* Time From Field */}
                      <tr>
                        <td style={{ textAlign: 'right', paddingRight: '10px', verticalAlign: 'middle' }}>
                          <label style={{ fontWeight: 'normal' }}>Time From <span style={{ color: 'red' }}>*</span></label>
                        </td>
                        <td>
                          <input
                            type="time"
                            name="time_from"
                            className="form-control"
                            required
                            value={formData.time_from}
                            onChange={handleInputChange}
                            style={{ width: 'auto', minWidth: '120px' }}
                          />
                        </td>
                      </tr>

                      {/* Time To Field */}
                      <tr>
                        <td style={{ textAlign: 'right', paddingRight: '10px', verticalAlign: 'middle' }}>
                          <label style={{ fontWeight: 'normal' }}>Time To <span style={{ color: 'red' }}>*</span></label>
                        </td>
                        <td>
                          <input
                            type="time"
                            name="time_to"
                            className="form-control"
                            required
                            value={formData.time_to}
                            onChange={handleInputChange}
                            style={{ width: 'auto', minWidth: '120px' }}
                          />
                        </td>
                      </tr>

                      {/* Description Field */}
                      <tr>
                        <td style={{ textAlign: 'right', paddingRight: '10px', verticalAlign: 'top' }}>
                          <label style={{ fontWeight: 'normal' }}>Description</label>
                        </td>
                        <td>
                          <textarea
                            name="description"
                            className="form-control"
                            rows="3"
                            value={formData.description}
                            onChange={handleInputChange}
                            style={{ width: '100%' }}
                          />
                        </td>
                      </tr>

                      {/* Updated At Field */}
                      <tr>
                        <td style={{ textAlign: 'right', paddingRight: '10px', verticalAlign: 'middle' }}>
                          <label style={{ fontWeight: 'normal' }}>Updated At</label>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            disabled
                            value={formData.updated_at || 'N/A'}
                            style={{ width: 'auto', minWidth: '180px', backgroundColor: '#eee' }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'space-between' }}>
                    <button
                      type="button"
                      onClick={handleDeleteInspection}
                      disabled={formLoading}
                      style={{ backgroundColor: '#d9534f', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '3px', cursor: 'pointer' }}
                    >
                      {formLoading ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                      type="submit"
                      disabled={formLoading}
                      style={{ backgroundColor: '#1B2046', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '3px', cursor: 'pointer' }}
                    >
                      {formLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </form>
              </div>
              <div className="modal-footer" style={{ backgroundColor: '#f5f5f5', padding: '10px 15px', textAlign: 'right' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingInspection(null);
                    setPropertySearchValue('');
                    setShowPropertyTypeahead(false);
                  }}
                  style={{ backgroundColor: '#5bc0de', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '3px', cursor: 'pointer' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuildingManagerCalendar;

